import type { ColorTokens } from "tamagui";

import { sizeTokens } from "../../themes/token-size";
import { pressVariants } from "../Motion/motion";
import type { ButtonSize, ButtonVariant } from "./types";

export const buttonVariants = {
  variant: {
    primary: {
      backgroundColor: "$surfaceStrong",
      borderWidth: 0,
      hoverStyle: {
        backgroundColor: "$surfaceStrongHover",
      },
      pressStyle: {
        backgroundColor: "$surfaceStrongHover",
      },
      focusVisibleStyle: {
        outlineWidth: 2,
        outlineColor: "$borderInverse",
        outlineStyle: "solid",
        outlineOffset: 2,
      },
    },
    brand: {
      backgroundColor: "$bgBrandStrong",
      borderWidth: 0,
      boxShadow: "inset 0px -1px 4px 0px $utilityOverlayMedium",
      hoverStyle: {
        backgroundColor: "$bgBrandStrong",
        boxShadow: "inset 0px -1px 4px 0px $utilityOverlayMedium",
      },
      pressStyle: {
        backgroundColor: "$bgBrandStrong",
        boxShadow: "inset 0px -1px 4px 0px $utilityOverlayMedium",
      },
      focusVisibleStyle: {
        outlineWidth: 2,
        outlineColor: "$borderInverse",
        outlineStyle: "solid",
        outlineOffset: 2,
      },
    },
    secondary: {
      backgroundColor: "$surface2",
      borderWidth: 1,
      borderColor: "$borderDefault",
      hoverStyle: {
        backgroundColor: "$surface2Hover",
        borderColor: "$borderDefaultHover",
      },
      pressStyle: {
        backgroundColor: "$surface2Hover",
        borderColor: "$borderDefaultHover",
      },
      focusVisibleStyle: {
        outlineWidth: 2,
        outlineColor: "$borderStrong",
        outlineStyle: "solid",
        outlineOffset: 2,
      },
    },
    tertiary: {
      backgroundColor: "$transparent",
      borderWidth: 0,
      hoverStyle: {
        backgroundColor: "$surface1Hover",
      },
      pressStyle: {
        backgroundColor: "$surface1Hover",
      },
      focusVisibleStyle: {
        outlineWidth: 2,
        outlineColor: "$borderStrong",
        outlineStyle: "solid",
        outlineOffset: 2,
      },
    },
  },
  size: {
    large: {
      height: "$si7",
      paddingHorizontal: "$sp4",
    },
    medium: {
      height: "$si5",
      paddingHorizontal: "$sp2.5",
    },
    small: {
      height: "$si4",
      paddingHorizontal: "$sp2",
    },
  },
  disabled: {
    true: {
      opacity: 1,
      cursor: "not-allowed",
    },
  },
  fillWidth: {
    true: {
      width: "100%",
    },
    false: {
      width: "auto",
      minWidth: "auto",
    },
  },
  press: pressVariants,
} as const;

export const textColorTokens: Record<ButtonVariant, ColorTokens> = {
  primary: "$textInverse",
  brand: "$textOnBrand",
  secondary: "$textDefault",
  tertiary: "$textDefault",
};

export const disabledTextColorTokens: Record<ButtonVariant, ColorTokens> = {
  primary: "$textSubtle",
  brand: "$textSubtle",
  secondary: "$textSubtle",
  tertiary: "$textMuted",
};

export const iconColorTokens: Record<ButtonVariant, ColorTokens> = {
  primary: "$iconInverse",
  brand: "$iconOnBrand",
  secondary: "$iconDefault",
  tertiary: "$iconDefault",
};

export const disabledIconColorTokens: Record<ButtonVariant, ColorTokens> = {
  primary: "$iconSubtle",
  brand: "$iconSubtle",
  secondary: "$iconSubtle",
  tertiary: "$iconMuted",
};

// `tone="dark"` overrides, applied over the variant's own styles. Only tertiary
// is defined: it is the one variant that breaks on `$surfaceInverse`, since its
// neutral glyph and near-white hover surface both disappear against green900.
// Brand and secondary already read there; primary does not, but nothing renders
// one on a dark surface yet — ENG-8181 covers the rest.
export const darkToneVariantStyles: Partial<Record<ButtonVariant, object>> = {
  tertiary: {
    hoverStyle: { backgroundColor: "$utilityOverlayInverse" },
    pressStyle: { backgroundColor: "$utilityOverlayInverse" },
    focusVisibleStyle: {
      outlineWidth: 2,
      outlineColor: "$borderInverse",
      outlineStyle: "solid",
      outlineOffset: 2,
    },
  },
};

export const darkToneTextColorTokens: Partial<
  Record<ButtonVariant, ColorTokens>
> = {
  tertiary: "$textInverse",
};

export const darkToneIconColorTokens: Partial<
  Record<ButtonVariant, ColorTokens>
> = {
  tertiary: "$iconInverse",
};

export const buttonSizeToIconToken: Record<
  ButtonSize,
  `$icon${Capitalize<ButtonSize>}`
> = {
  large: "$iconLarge",
  medium: "$iconMedium",
  small: "$iconSmall",
};

export const iconPaddingBySize = {
  large: { iconSide: "$sp3", textSide: "$sp4" },
  medium: { iconSide: "$sp1.5", textSide: "$sp2.5" },
  small: { iconSide: "$sp1", textSide: "$sp2" },
} as const;

export const disabledVariantStyles: Record<ButtonVariant, object> = {
  primary: {
    backgroundColor: "$bgDisabledSubtle",
    hoverStyle: { backgroundColor: "$bgDisabledSubtle" },
    pressStyle: { backgroundColor: "$bgDisabledSubtle", scale: 1 },
  },
  brand: {
    backgroundColor: "$bgDisabledSubtle",
    boxShadow: "none",
    hoverStyle: {
      backgroundColor: "$bgDisabledSubtle",
      boxShadow: "none",
    },
    pressStyle: {
      backgroundColor: "$bgDisabledSubtle",
      boxShadow: "none",
      scale: 1,
    },
  },
  secondary: {
    backgroundColor: "$bgDisabledSubtle",
    borderColor: "$borderStrong",
    hoverStyle: {
      backgroundColor: "$bgDisabledSubtle",
      borderColor: "$borderStrong",
    },
    pressStyle: {
      backgroundColor: "$bgDisabledSubtle",
      borderColor: "$borderStrong",
      scale: 1,
    },
  },
  tertiary: {
    backgroundColor: "$transparent",
    hoverStyle: { backgroundColor: "$transparent" },
    pressStyle: { backgroundColor: "$transparent", scale: 1 },
  },
} as const;

// Square sizes for icon-only buttons (width = height)
export const iconOnlySizes: Record<ButtonSize, number> = {
  large: sizeTokens.si7,
  medium: sizeTokens.si5,
  small: sizeTokens.si4,
};
